// Floating quiz teaser popup — appears after 30s on the home page.
// Dismissal persisted in localStorage. Mobile-friendly bottom-right card.
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { X, Sparkles, ArrowRight } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";

const STORAGE_KEY = "vpv_quiz_popup_v1";
const DELAY_MS = 30000;

export default function QuizPopup() {
  const { lang } = useLang();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Only on home and blog pages, never on /quiz itself or /admin
    if (pathname.startsWith("/quiz") || pathname.startsWith("/admin")) return;
    if (localStorage.getItem(STORAGE_KEY)) return;
    const t = setTimeout(() => setOpen(true), DELAY_MS);
    return () => clearTimeout(t);
  }, [pathname]);

  const dismiss = () => {
    setOpen(false);
    try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch {}
  };

  if (!open) return null;

  const T = lang === "es" ? {
    eyebrow: "Quiz · 2 min",
    title: "¿Qué tipo de vacacionista eres?",
    body: "Descubre tu perfil Pura Vida y qué llevar para disfrutarlo al máximo.",
    cta: "Hacer el quiz",
    discount: "+ 10% off al reservar directo",
  } : {
    eyebrow: "Quiz · 2 min",
    title: "What kind of vacationer are you?",
    body: "Discover your Pura Vida profile and the welcome kit we'll prepare for you.",
    cta: "Take the quiz",
    discount: "+ 10% off when you book direct",
  };

  return (
    <div
      data-testid="quiz-popup"
      className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-[60] w-[calc(100%-2rem)] sm:w-[400px] animate-quiz-popup-enter"
      style={{
        animation: "quizPopupEnter 0.6s cubic-bezier(0.16, 1, 0.3, 1) both",
      }}
    >
      <style>{`
        @keyframes quizPopupEnter {
          0% { opacity: 0; transform: translateY(24px) scale(0.96); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes quizPopupPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(224, 122, 95, 0.4); }
          50% { box-shadow: 0 0 0 12px rgba(224, 122, 95, 0); }
        }
      `}</style>
      <div className="relative bg-white border-2 border-stone-900 shadow-2xl">
        <button
          onClick={dismiss}
          data-testid="quiz-popup-close"
          className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center text-stone-400 hover:text-stone-900 transition-colors z-10"
          aria-label="Cerrar"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Decorative band */}
        <div className="bg-[#FCEFE6] px-6 pt-6 pb-4 border-b border-stone-200">
          <div className="flex items-center gap-2 mb-3">
            <span className="inline-flex items-center justify-center w-7 h-7 bg-[#E07A5F] text-white" style={{ animation: "quizPopupPulse 2s ease-in-out infinite" }}>
              <Sparkles className="w-4 h-4" />
            </span>
            <p className="text-[10px] tracking-[0.25em] uppercase text-[#E07A5F] font-semibold">{T.eyebrow}</p>
          </div>
          <div className="flex gap-2 text-3xl">
            <span>🌅</span><span>🏖️</span><span>🍷</span><span>🎉</span>
          </div>
        </div>

        <div className="px-6 py-5">
          <h3 className="font-serif-display text-2xl leading-tight text-stone-900 mb-2">
            {T.title}
          </h3>
          <p className="text-sm text-stone-600 leading-relaxed mb-5">
            {T.body}
          </p>

          <Link
            to="/quiz"
            onClick={dismiss}
            data-testid="quiz-popup-cta"
            className="group flex items-center justify-between w-full bg-stone-900 hover:bg-[#E07A5F] text-white px-5 py-4 text-xs uppercase tracking-[0.18em] font-semibold transition-colors"
          >
            <span>{T.cta}</span>
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </Link>

          <p className="mt-3 text-[11px] text-center text-[#3F6A53] font-medium tracking-wide">
            ✦ {T.discount} ✦
          </p>
        </div>
      </div>
    </div>
  );
}
